import { Box, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export const StyledBox = styled(Box)(() => ({
  width: '100%',
  marginTop: 40,
  '@media (max-height: 820px)': {
    marginTop: 20,
  },
}));

interface CalendarBoxProps {
  view: string;
}

export const CalendarBox = styled(Box)<CalendarBoxProps>(({ view, theme }) => ({
  '.fc ': {
    height: '680px',
    '@media (max-height: 820px)': {
      height: 590,
    },
  },
  '.fc td': {
    border: view === 'dayGridMonth' ? '1px solid #B8B8B9' : '',
  },
  '.fc-button-group': {
    '.fc-button:not(:first-of-type)': {
      display: 'none',
    },
  },
  '.fc-scroller': {
    overflow: 'auto !important',
  },
  '.fc-scroller::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: '#DDDDDD',
  },
  '.fc-theme-standard .fc-scrollgrid': {
    border: '0px',
  },
  '.fc-scroller::-webkit-scrollbar-thumb ': {
    borderRadius: '12px',
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  '.fc-theme-standard th': {
    border: 'none',
  },
  '.fc-toolbar-title': {
    marginLeft: '90px',
    textTransform: 'capitalize',
    '@media (max-height: 820px)': {
      fontSize: '1.5rem',
    },
  },
  '.fc-toolbar.fc-header-toolbar': {
    marginBottom: '0',
  },
  '.fc-button-primary': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
    border: 'none',
    transition: 'transform 0.3s ease',
    fontSize: '1.2rem',
    padding: '4px',
    margin: '4px',
    ':focus': {
      boxShadow: 'none !important',
    },
    ':active, :hover': {
      borderRadius: '6px !important',
      backgroundColor: `${theme.palette.secondary.main} !important`,
    },
    '@media (max-height: 820px)': {
      fontSize: '1rem',
    },
  },
  '.fc-col-header': {
    margin: '8px 0',
  },
  '.fc-col-header-cell-cushion': {
    padding: '8px',
    fontSize: '16px',
    fontWeight: '500',
    '@media (max-height: 820px)': {
      padding: 0,
      fontSize: '14px',
    },
  },
  '.fc-col-header-cell.fc-day.fc-day-today': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 10,
  },
  '.fc-timegrid-body': {
    borderLeft: '1px solid #DDDDDD',
    marginTop: '2px',
  },
  '.fc-timegrid-slot-label-frame': {
    backgroundColor: '#ECF0F6',
  },
  '.fc-timegrid-slot-label-cushion': {
    color: theme.palette.primary.dark,
    fontWeight: '600',
    padding: '8px',
  },
  '.fc-timegrid-col.fc-day-today ': {
    background:
      view === 'timeGridDay'
        ? 'transparent !important'
        : `${alpha(theme.palette.primary.main, 0.1)} !important`,
    border: 'none !important',
  },
  '.fc-timegrid-event-harness>.fc-timegrid-event': {
    background: 'transparent',
    fontSize: '12px',
  },
  '.fc-today-button': {
    backgroundColor: theme.palette.secondary.main,
    color: '#FAFCFF',
    padding: '4px 12px',
    borderRadius: '4px !important',
    ':disabled': {
      backgroundColor: theme.palette.secondary.main,
      opacity: 0.5,
    },
    ':hover': {
      backgroundColor: theme.palette.secondary.main,
      filter: 'brightness(85%)',
    },
  },
  '.fc-daygrid-day.fc-day-today': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
    color: theme.palette.secondary.main,
  },
  '.fc-event fc-event-start .fc-event-end .fc-event-past .fc-daygrid-event .fc-daygrid-dot-event':
    {
      ':hover': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
  '.more-events': {
    color: theme.palette.secondary.main,
  },
  '.fc-popover': {
    backgroundColor: '#FAFCFF',
    color: 'black',
    zIndex: 1,
  },
  '.fc-v-event': {
    backgroundColor: 'transparent',
    color: '#FAFCFF !important',
    borderWidth: '2px',
  },
  '.fc-timegrid-event-harness': {
    margin: 0,
    minHeight: '38px',
  },
  '.fc-direction-ltr .fc-timegrid-more-link': {
    left: 0,
    height: 25,
    marginTop: 40,
    textAlign: 'center',
    color: theme.palette.secondary.main,
  },
  '.fc-direction-ltr .fc-timegrid-col-events': {
    margin: 0,
  },
  // eslint-disable-next-line max-len
  '.fc-direction-ltr .fc-daygrid-event.fc-event-end, .fc-direction-rtl .fc-daygrid-event.fc-event-start':
    {
      overflow: 'hidden',
      backgroundColor: 'transparent',
      border: 'none',
    },

  '.fc-timegrid-event .fc-event-main': {
    overflow: 'hidden !important',
    backgroundColor: '#ECF0F6',
  },
  '.fc-h-event': {
    backgroundColor: 'transparent',
    border: 'none',
  },
}));

interface EventProps {
  color: string;
}

export const EventBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '8px',
  alignItems: 'flex-start',
  width: 'fit-content',
  cursor: 'pointer',
  p: {
    whiteSpace: 'nowrap',
    margin: 0,
    color: '#76787B',
  },
}));

export const EventBlockage = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '8px',
  alignItems: 'flex-start',
  cursor: 'pointer',
  width: '100%',
  height: '100%',
  p: {
    whiteSpace: 'nowrap',
    margin: 0,
    color: '#76787B',
  },
}));

export const EventDisabledBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '8px',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  cursor: 'default',
  p: {
    whiteSpace: 'nowrap',
    margin: 0,
    color: '#76787B',
  },
}));

export const LabelBox = styled(Box)<EventProps>(({ color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  p: {
    fontWeight: '600',
    color: '#76787B',
  },
  '.material-symbols-outlined': {
    color,
    fontWeight: 600,
    fontSize: '18px !important',
  },
}));

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ECF0F6',
    color: '#76787B',
    fontSize: 12,
    fontWeight: '400',
    padding: '2px 6px',
    marginTop: '4px !important',
    marginBottom: '2px !important',
  },
}));

export const ColorDot = styled(Box)<EventProps>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: 50,
  background: color,
}));
